import React, { useState } from "react";
import { Modal } from "@/components/Modal";
import { eraLabel } from "@/lib/util";
import { Heart, MapPin, Package, ArrowRight } from "lucide-react";

const contentLabel = (item) => {
  if (item.kind === "bag") return "What's Inside My School Bag?";
  if (item.kind === "geometry") return "Inside the Box";
  if (item.kind === "tiffin") return "Tiffin Memories";
  if (item.kind === "notebook") return "How We Covered It";
  return "Inside";
};

export const SchoolItemCard = ({ item, open, onClose, unlocked, onAdd }) => {
  const [popped, setPopped] = useState(false);
  if (!item) return null;
  const img = item.image_url || (item.images && item.images[0]);
  const isGame = item.category === "game";
  const isNotebook = item.kind === "notebook";
  const handleAdd = () => { onAdd(item.id); setPopped(true); setTimeout(() => setPopped(false), 500); };

  return (
    <Modal open={open} onClose={onClose} testId="school-card" maxWidth="max-w-md">
      <div className="p-5 sm:p-7">
        <div className="relative mb-3 flex h-44 items-center justify-center overflow-hidden rounded-md border-[3px] border-kirana-brown"
          style={{ background: img ? "#FDF6E3" : `linear-gradient(145deg, ${item.placeholder_color}, ${item.placeholder_color}cc)` }}>
          {img ? <img src={img} alt={item.name} className="h-full w-full object-contain" /> : (
            <span className="font-signboard px-3 text-center text-2xl font-bold text-white drop-shadow">{item.name}</span>
          )}
        </div>

        <h2 data-testid="school-card-name" className="font-signboard text-3xl font-bold leading-none text-kirana-brown">{item.name}</h2>
        <div className="mt-2 flex flex-wrap gap-2">
          <span className="rounded border-2 border-kirana-brown bg-kirana-cream px-2 py-0.5 text-xs font-bold text-kirana-brown capitalize">{item.category}</span>
          <span className="rounded border-2 border-kirana-brown bg-kirana-green px-2 py-0.5 text-xs font-bold text-white">{eraLabel(item.era_start)}</span>
        </div>

        {item.memory && (
          <p className="mt-3 rounded-md border-2 border-dashed border-kirana-border bg-[repeating-linear-gradient(#FDF6E3,#FDF6E3_22px,#cdd4e6_23px)] p-3 text-sm italic text-kirana-brown">
            "{item.memory}"
          </p>
        )}

        {/* games: how to play */}
        {isGame && item.how_to_play?.length > 0 && (
          <>
            <h3 className="mt-4 font-signboard text-lg font-bold text-kirana-brown">How To Play</h3>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {item.how_to_play.map((s, i) => (
                <div key={s} className="flex items-start gap-2 rounded-md border-2 border-kirana-border bg-card p-2">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-kirana-brown bg-kirana-gold text-xs font-bold text-kirana-brown">{i + 1}</span>
                  <span className="text-sm text-kirana-brown">{s}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-3 text-sm text-kirana-brown">
              {item.what_you_needed && <span className="inline-flex items-center gap-1 rounded border-2 border-kirana-border bg-kirana-cream px-2 py-1"><Package className="h-3.5 w-3.5" /> {item.what_you_needed}</span>}
              {item.where_played && <span className="inline-flex items-center gap-1 rounded border-2 border-kirana-border bg-kirana-cream px-2 py-1"><MapPin className="h-3.5 w-3.5" /> {item.where_played}</span>}
            </div>
          </>
        )}

        {/* objects: contents / sequence */}
        {!isGame && item.contents?.length > 0 && (
          <>
            <h3 className="mt-4 font-signboard text-lg font-bold text-kirana-brown">{contentLabel(item)}</h3>
            {isNotebook ? (
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {item.contents.map((c, i) => (
                  <React.Fragment key={c}>
                    <span className="rounded-md border-2 border-kirana-brown bg-kirana-cream px-2 py-1 text-xs font-bold text-kirana-brown">{c}</span>
                    {i < item.contents.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-kirana-brown/50" />}
                  </React.Fragment>
                ))}
                <div className="mt-3 w-full rounded-md border-2 border-kirana-brown bg-[#B08968] p-2 text-kirana-cream">
                  <div className="rounded border border-dashed border-kirana-cream/70 p-2 text-xs">
                    NAME: __________<br />CLASS: ____ · SEC: ____ · ROLL NO: ____
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                {item.contents.map((c) => (
                  <span key={c} className="rounded-md border-2 border-kirana-border bg-kirana-cream px-2 py-1 text-xs font-semibold text-kirana-brown">{c}</span>
                ))}
              </div>
            )}
          </>
        )}

        <div className="mt-5 flex justify-center">
          {unlocked ? (
            <div data-testid="school-memory-unlocked" className="flex items-center gap-2 rounded-md border-2 border-kirana-red bg-kirana-red/10 px-4 py-2 font-bold text-kirana-red">
              <Heart className={`h-5 w-5 fill-kirana-red ${popped ? "animate-heart-pop" : ""}`} /> MEMORY UNLOCKED
            </div>
          ) : (
            <button data-testid="school-memory-add-btn" onClick={handleAdd}
              className="flex items-center gap-2 rounded-md border-[3px] border-kirana-brown bg-kirana-red px-5 py-2.5 font-bold text-white transition-transform hover:scale-[1.03] hard-shadow">
              <Heart className="h-5 w-5" /> Add to My Memories
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};
